import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { Reveal, SectionHeading } from "./Reveal";
import { GALLERY_ITEMS } from "../data/media";
import { ImageLightbox } from "./ImageLightbox";

export const Gallery = () => {
  const { t } = useLang();
  const [filter, setFilter] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  const filtered = useMemo(
    () => (filter === 0 ? GALLERY_ITEMS : GALLERY_ITEMS.filter((g) => g.category === filter)),
    [filter]
  );

  return (
    <section id="gallery" className="relative py-24 md:py-32 bg-[#0A0A0A]" data-testid="gallery-section">
      <div className="px-6 md:px-12 lg:px-20">
        <SectionHeading overline={t.gallery.overline} title={t.gallery.title} text={t.gallery.text} center />

        <Reveal delay={0.1}>
          <div className="mt-12 flex flex-wrap justify-center gap-2">
            {t.gallery.filters.map((f, i) => (
              <button
                key={i}
                onClick={() => setFilter(i)}
                className={`px-4 py-2 text-xs uppercase tracking-[0.13em] border transition-colors duration-300 ${
                  filter === i
                    ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5"
                    : "border-[#2E2E2E] text-[#FFFFF0]/55 hover:border-white/30"
                }`}
                data-testid={`gallery-filter-${i}`}
              >
                {f}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="mt-10 columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
          <AnimatePresence>
            {filtered.map((item, i) => (
              <motion.div
                key={item.src + i}
                layout
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: 0.03 * i }}
                className="mb-5 break-inside-avoid group relative overflow-hidden border border-[#2E2E2E] cursor-zoom-in"
                onClick={() => setLightbox(i)}
                data-testid={`gallery-item-${i}`}
              >
                <img
                  src={item.src}
                  alt={`Phantom Residence ${i + 1}`}
                  className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${
                    item.span === "tall" ? "aspect-[3/4]" : "aspect-[4/3]"
                  }`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      {lightbox !== null && (
        <ImageLightbox
          items={filtered.map((g) => g.src)}
          index={lightbox}
          onClose={() => setLightbox(null)}
          onPrev={() => setLightbox((p) => (p - 1 + filtered.length) % filtered.length)}
          onNext={() => setLightbox((p) => (p + 1) % filtered.length)}
          downloadLabel={t.plans.download}
        />
      )}
    </section>
  );
};
